'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  characterData: {
    name: string;
    title: string;
    level: number;
    itemLevel: number;
    spec: string;
    class: string;
    realm: string;
    guild: string;
    image: string;
    alt: string;
  };
}

const HeroSection = ({ characterData }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setRotation((prev) => (prev + 0.5) % 360);
    }, 50);

    return () => clearInterval(interval);
  }, [isHydrated]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden ">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-frost-blue rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blood-red rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{marginTop: "50px"}}>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Character Model Section */}
          <div className="relative">
            <div className="relative w-full aspect-square max-w-lg mx-auto">
              {/* Rotating Ring Effect */}
              <div
                className="absolute inset-0 border-4 border-frost-blue rounded-full opacity-30"
                style={{
                  transform: isHydrated ? `rotate(${rotation}deg)` : 'rotate(0deg)',
                  transition: 'transform 0.05s linear',
                }}
              >
                <div className="absolute top-0 left-1/2 w-4 h-4 bg-frost-blue rounded-full -translate-x-1/2 -translate-y-1/2 shadow-glow-blue"></div>
                <div className="absolute bottom-0 left-1/2 w-4 h-4 bg-blood-red rounded-full -translate-x-1/2 translate-y-1/2 shadow-glow-red"></div>
              </div>

              {/* Character Image */}
              <div className="absolute inset-8 rounded-full overflow-hidden border-4 border-metallic-silver shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-frost-blue/20 to-blood-red/20"></div>
                <AppImage
                  src={characterData.image}
                  alt={characterData.alt}
                  className="absolute w-full h-full object-cover"
                />
              </div>

            {/* Stats Orbits */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-frost-blue rounded-lg px-4 py-2 shadow-glow-blue">
                <p className="text-xs font-rajdhani text-muted-foreground">Item Level</p>
                <p className="text-2xl font-orbitron font-bold text-frost-blue">{characterData.itemLevel}</p>
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 bg-card border border-blood-red rounded-lg px-4 py-2 shadow-glow-red">
                <p className="text-xs font-rajdhani text-muted-foreground">Level</p>
                <p className="text-2xl font-orbitron font-bold text-blood-red">{characterData.level}</p>
              </div>
            </div>
            </div>
          </div>

          {/* Character Info Section */}
          <div className="space-y-8">
            {/* Name & Title */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="h-1 w-12 bg-gradient-to-r from-blood-red to-transparent"></div>
                <h1 className="font-orbitron text-5xl lg:text-7xl font-bold">
                  <span className="text-metallic-silver">Fabbz</span>
                </h1>
              </div>
              <p className="font-cinzel text-2xl text-frost-blue tracking-wider">{characterData.title}</p>
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="ShieldCheckIcon" size={20} className="text-frost-blue" />
                <span className="font-rajdhani text-lg">{characterData.spec} {characterData.class}</span>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface border border-border rounded-lg p-4 hover:border-frost-blue transition-colors">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="ServerIcon" size={18} className="text-frost-blue" />
                  <p className="text-xs font-rajdhani text-muted-foreground uppercase tracking-wider">Realm</p>
                </div>
                <p className="font-inter text-lg text-foreground">{characterData.realm}</p>
              </div>
              <div className="bg-surface border border-border rounded-lg p-4 hover:border-frost-blue transition-colors">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="UserGroupIcon" size={18} className="text-blood-red" />
                  <p className="text-xs font-rajdhani text-muted-foreground uppercase tracking-wider">Guild</p>
                </div>
                <p className="font-inter text-lg text-foreground">{characterData.guild}</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              
              <button className="flex items-center space-x-2 px-6 py-3 bg-surface border-2 border-frost-blue rounded-lg font-rajdhani font-semibold text-frost-blue hover:bg-frost-blue hover:text-background transition-all duration-300 group">
                <Icon name="TrophyIcon" size={20} className="group-hover:scale-110 transition-transform" />
                <span><a target="_blank" href="https://worldofwarcraft.blizzard.com/en-us/character/us/illidan/fabbz/achievements">Achievements</a></span>
              </button>
            </div>

            {/* Verification Badges */}
            <div className="flex flex-wrap gap-3">
              {[{name:'Warcraft Logs', link:'https://www.warcraftlogs.com/character/us/illidan/fabbz'},{name:'Raider.io', link:"https://raider.io/characters/us/illidan/Fabbz"},{name:'Battle.net', link:"https://worldofwarcraft.blizzard.com/en-us/character/us/illidan/fabbz"}].map((platform) => (
                <div
                  key={platform.name}
                  className="flex items-center space-x-2 px-4 py-2 bg-card border border-success rounded-full hover:border-frost-blue transition-colors group"
                >
                  <Icon name="CheckBadgeIcon" size={16} className="text-success group-hover:text-frost-blue transition-colors" />
                  <a href={platform.link} target="_blank" className="text-xs font-inter text-muted-foreground group-hover:text-foreground transition-colors">{platform.name}</a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .shadow-glow-blue {
          box-shadow: 0 0 20px rgba(0, 204, 255, 0.6);
        }
        .shadow-glow-red {
          box-shadow: 0 0 20px rgba(204, 0, 0, 0.6);
        }
      `}</style>
    </section>
  );
};

export default HeroSection;